import type { CSSProperties } from "react";

// Reusable loading primitives. Two atoms:
//   - <Spinner />: inline indeterminate progress, sized to match adjacent text.
//   - <Skeleton />: pulsing placeholder block for list/table rows during fetch.
// Both rely on Tailwind's built-in animate-* keyframes so there's nothing
// extra to register in the global CSS.

type SpinnerProps = {
  // Pixel size; default sits well next to base-size text.
  size?: number;
  className?: string;
  label?: string;
};

export function Spinner({ size = 14, className = "", label }: SpinnerProps) {
  const style: CSSProperties = { width: size, height: size };
  return (
    <span
      role={label ? "status" : undefined}
      aria-label={label}
      className={`inline-block ${className}`}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="animate-spin text-current"
        style={style}
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeOpacity="0.2"
          strokeWidth="3"
        />
        <path
          d="M12 2a10 10 0 0 1 10 10"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

// Convenience for buttons: a spinner that sits where a label would, plus
// the label text. Caller controls visibility via the parent's `busy` flag.
export function ButtonSpinner({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <Spinner size={14} />
      <span>{label}</span>
    </span>
  );
}

type SkeletonProps = {
  className?: string;
  // Honors prefers-reduced-motion via Tailwind's motion-safe variant.
  rounded?: "sm" | "md" | "lg" | "full";
};

export function Skeleton({ className = "", rounded = "md" }: SkeletonProps) {
  const roundedClass =
    rounded === "full"
      ? "rounded-full"
      : rounded === "lg"
        ? "rounded-lg"
        : rounded === "sm"
          ? "rounded-sm"
          : "rounded-md";
  return (
    <div
      aria-hidden="true"
      className={`${roundedClass} bg-neutral-200/70 motion-safe:animate-pulse ${className}`}
    />
  );
}

// Centered page-level loading state, used while a route's primary data
// resolves. Cleaner than a bare "Loading…" string.
export function PageSpinner({ label = "Loading" }: { label?: string }) {
  return (
    <div
      role="status"
      aria-label={label}
      className="flex items-center gap-2 px-1 py-2 text-sm text-neutral-500"
    >
      <Spinner size={14} />
      <span>{label}…</span>
    </div>
  );
}
